import * as filepath from "./filepath";

import { FS, fsCopyProps } from "./fs";
import { FsDir } from "./fsentry";
import {
  XhrOpts,
  xhrAsync,
  xhrDeleteAuth,
  xhrGetJSONAuth,
  xhrPut,
} from "./http";
import { dateStringToMs, len, throwIf } from "./util";
import {
  msGraphEndpointV1,
  onedriveMaybeUpdateAccessToken,
} from "./fs-onedrive-login";

import type { HttpProgress } from "./http";
import type { OneDriveConfig } from "./fs";
import type { Writable } from "svelte/store";

// One Drive props, files and dirs:
const prop_od_createDateTime = "createDateTime";
const prop_od_cTag = "cTag";
const prop_od_eTag = "eTag";
export const prop_od_webUrl = "webUrl";
export const prop_od_parentReference_id = "|parentReference.id";
export const prop_od_parentReference_driveId = "|parentReference.driveId";

// files only
export const prop_od_dlURL = "@microsoft.graph.downloadUrl";
export const prop_od_file_mimeType = "|file.mimeType";
export const prop_od_file_hashes_sha1Hash = "|file.hashes.sha1Hash";
export const prop_od_file_hashes_sha256Hash = "|file.hashes.sha256Hash";

const fileDirsProps = [
  prop_od_createDateTime,
  prop_od_cTag,
  prop_od_eTag,
  prop_od_webUrl,
  prop_od_parentReference_id,
  prop_od_parentReference_driveId,
];

const fileOnlyProps = [
  prop_od_dlURL,
  prop_od_file_mimeType,
  prop_od_file_hashes_sha1Hash,
  prop_od_file_hashes_sha256Hash,
];

/* TODO:
folder.view.viewType: "thumbnail"
folder.view.sortBy: "name"
folder.view.sortOrder: "ascending", "descending"

files:
file.hashes.quickXorHash
*/

class FileSystemOneDrive extends FS {
  supportsDirRename = true;

  constructor(config: OneDriveConfig) {
    super(config);
  }

  get oneDriveConfig(): OneDriveConfig {
    return this.config as OneDriveConfig;
  }

  async updateAccessToken() {
    let c = this.oneDriveConfig;
    let didUpdate = await onedriveMaybeUpdateAccessToken(c);
    if (!didUpdate) {
      return c.accessToken;
    }
    console.log("onedrive.updateAccessToken: found new token:", c.accessToken);
    // TODO: update bookmarks
    return c.accessToken;
  }

  async readDir(
    dirPath: string,
    force: boolean,
    progress: Writable<any>
  ): Promise<FsDir> {
    return this.loadDir(dirPath, force, progress, async () => {
      if (dirPath != "/") {
        await this.readParentDirs(dirPath, force, progress);
      }
      let parentId = "root";
      if (dirPath != "/") {
        let found = this.findFirstEntryOfType(dirPath, true);
        parentId = found.dir.entryId(found.entry);
      }
      await this.updateAccessToken();
      let t = this.oneDriveConfig.accessToken;
      let entries = await oneDriveGetEntriesInDir(t, dirPath, parentId);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  getParentDirectoryId(path: string) {
    let dn = filepath.split(path);
    if (dn.dir == "/") {
      return "root";
    }
    let found = this.findFirstEntryOfType(dn.dir, true);
    return found.dir.entryId(found.entry);
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress) {
    console.log(`FileSystemOneDrive.uploadFile: ${path}`);
    let parentId = this.getParentDirectoryId(path);
    let dn = filepath.split(path);
    await this.updateAccessToken();
    let accessToken = this.oneDriveConfig.accessToken;

    // https://docs.microsoft.com/en-us/graph/api/driveitem-put-content?view=graph-rest-1.0&tabs=http
    let uploadURL = `${msGraphEndpointV1}/me/drive/items/${parentId}:/${dn.name}:/content`;
    await xhrPut(uploadURL, file, httpProgress, accessToken);
  }

  async getPublicURL(path: string): Promise<string> {
    console.log(`FileSystemOneDrive.getPublicURL: path=${path}`);
    path = filepath.stripDirSlash(path);
    let all = this.findEntries(path);
    let e = all.entries[0];
    let url = all.dir.entryMeta(e, prop_od_dlURL);
    console.log("getPublicURL:", url);
    return url;
  }

  async createDirectory(dirPath: string) {
    console.log(`FileSystemOneDrive.createDirectory: ${dirPath}`);
    let parentId = this.getParentDirectoryId(dirPath);
    let dn = filepath.split(dirPath);
    let dirName = dn.name;
    await this.updateAccessToken();
    let accessToken = this.oneDriveConfig.accessToken;

    let createURL = `${msGraphEndpointV1}/me/drive/items/${parentId}/children`;
    let file = {
      name: dirName,
      folder: {},
      "@microsoft.graph.conflictBehavior": "rename",
    };
    let opts = new XhrOpts();
    opts.method = "POST";
    opts.headers["Authorization"] = `Bearer ${accessToken}`;
    opts.headers["Content-Type"] = `application/json`;
    opts.responseType = "json";
    opts.data = JSON.stringify(file);

    let res = await xhrAsync(createURL, opts);
    console.log("res:", res);
  }

  async rename(path: string, newPath: string) {
    console.log(`FileSystemOneDrive.rename: ${path} ${newPath}`);

    path = filepath.stripDirSlash(path);
    let all = this.findEntries(path);
    let e = all.entries[0];
    console.log(`rename: ${path}, isDir: ${all.dir.entryIsDir(e)}`);

    await this.updateAccessToken();

    // https://docs.microsoft.com/en-us/graph/api/driveitem-update?view=graph-rest-1.0&tabs=http
    let updateURL = `${msGraphEndpointV1}/me/drive/items/${all.dir.entryId(e)}`;
    let accessToken = this.oneDriveConfig.accessToken;
    let opts = new XhrOpts();
    opts.method = "PATCH";
    opts.responseType = "json";
    opts.headers = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": `application/json`,
    };
    let newName = filepath.base(newPath);
    let file = {
      name: newName,
    };
    opts.data = JSON.stringify(file);
    await xhrAsync(updateURL, opts);
  }

  async deleteFile(path: string) {
    console.log(`FileSystemOneDrive.deleteFile: ${path}`);
    path = filepath.stripDirSlash(path);
    let all = this.findEntries(path);
    let e = all.entries[0];
    console.log(`deleteFile: ${path}, isDir: ${all.dir.entryIsDir(e)}`);
    await this.updateAccessToken();
    // https://docs.microsoft.com/en-us/graph/api/driveitem-delete?view=graph-rest-1.0&tabs=http
    let accessToken = this.oneDriveConfig.accessToken;
    let deleteURL = `${msGraphEndpointV1}/me/drive/items/${all.dir.entryId(e)}`;
    await xhrDeleteAuth(deleteURL, accessToken);
  }
}

async function oneDriveGetEntriesInDir(
  accessToken: string,
  dir: string,
  dirId: string
): Promise<any[][]> {
  console.log(`oneDriveGetEntriesInDir: dirId: ${dirId}`);
  let js: any;
  let url = `${msGraphEndpointV1}/me/drive/items/${dirId}/children`;
  let entries: any[] = [];
  while (url) {
    console.log(`getting ${url}, nEntries so far: ${len(entries)}`);
    js = await xhrGetJSONAuth(url, accessToken, undefined);
    console.log("js:", js);
    entries.push(...js.value);
    url = js["@odata.nextLink"];
  }
  let wires: any[][] = [];
  for (let e of entries) {
    let isFile = e.hasOwnProperty("file");
    let isFolder = e.hasOwnProperty("folder");
    // TODO: this happens for "Personal Vault", not sure how to handle it
    let remoteItem = e.hasOwnProperty("remoteItem");
    if (remoteItem && (isFile || isFolder)) {
      throwIf(true, "not expecting remoteItem with file or folder");
    }
    if (!isFile && !isFolder) {
      if (remoteItem) {
        continue;
      }
      console.error("unknown entry:", e);
      throwIf(true, `${JSON.stringify(e)} should be either a file or a folder`);
    }

    let size = isFile ? e.size : -1;
    let lastMod = dateStringToMs(e.lastModifiedDateTime);
    let entries = isFile ? null : [];
    let meta = [e.id, e.name, size, lastMod, entries];
    fsCopyProps(fileDirsProps, e, meta);
    if (isFile) {
      fsCopyProps(fileOnlyProps, e, meta);
    }
    wires.push(meta);
  }
  return wires;
}

export function makeFileSystemOneDrive(c: OneDriveConfig): FileSystemOneDrive {
  console.log("makeFileSystemOneDrive, config:", c);
  let fs = new FileSystemOneDrive(c);
  return fs;
}
